import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { surahs, juzData } from '../data/quranData';
import { Search, ChevronDown, BookOpen } from 'lucide-react';

const TABS = ['surah', 'juz', 'chronological'];

const SurahBadge = ({ number }) => (
  <div className="surah-badge" data-testid={`surah-badge-${number}`}>
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <polygon
        points="30,2 54,16 54,44 30,58 6,44 6,16"
        fill="none"
        stroke="var(--gold)"
        strokeWidth="1.5"
      />
      <polygon
        points="30,8 48,19 48,41 30,52 12,41 12,19"
        fill="var(--gold)"
        fillOpacity="0.12"
        stroke="var(--gold)"
        strokeWidth="0.5"
      />
      <text
        x="30"
        y="33"
        textAnchor="middle"
        fill="var(--gold)"
        fontSize="16"
        fontWeight="600"
        fontFamily="Poppins, sans-serif"
      >
        {number}
      </text>
    </svg>
  </div>
);

const SurahCard = ({ surah, language }) => {
  const translationName = language === 'sv' ? surah.sv : surah.en;

  return (
    <Link
      to={`/quran/${surah.number}`}
      className="surah-card group"
      data-testid={`surah-card-${surah.number}`}
    >
      <div className="flex items-center gap-4">
        <SurahBadge number={surah.number} />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
            {surah.name}
          </h3>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {translationName}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {surah.ayahs} Ayahs
          </p>
          <p className="arabic-text text-sm text-gold opacity-70 mt-0.5">
            {surah.arabic}
          </p>
        </div>
      </div>
    </Link>
  );
};

const QuranPage = () => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('surah');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  const [showSort, setShowSort] = useState(false);

  const filteredSurahs = useMemo(() => {
    let list = [...surahs];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.en.toLowerCase().includes(q) ||
        s.sv.toLowerCase().includes(q) ||
        s.arabic.includes(q) ||
        String(s.number).includes(q)
      );
    }

    if (activeTab === 'chronological') {
      list.sort((a, b) => a.chronOrder - b.chronOrder);
    } else if (sortOrder === 'name') {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortOrder === 'ayahs') {
      list.sort((a, b) => b.ayahs - a.ayahs);
    } else if (sortOrder === 'revelation') {
      list.sort((a, b) => a.type.localeCompare(b.type));
    }

    return list;
  }, [searchQuery, activeTab, sortOrder]);

  const juzSurahs = useMemo(() => {
    return juzData.map(juz => ({
      ...juz,
      surahs: surahs.filter(s =>
        s.number >= juz.startSurah && s.number <= juz.endSurah
      )
    }));
  }, []);

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="quran-page">
      {/* Header */}
      <section className="py-16 islamic-pattern hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: 'var(--text-primary)' }} data-testid="quran-title">
            {t('quran.title')}
          </h1>
          <p style={{ color: 'var(--text-secondary)' }} className="max-w-2xl mx-auto">
            {t('quran.subtitle')}
          </p>
        </div>
      </section>

      <div className="gold-divider"></div>

      {/* Main Content */}
      <section className="py-10" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tabs + Search + Sort */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            {/* Tabs */}
            <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              {TABS.map(tab => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); setSortOrder('default'); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === tab
                      ? 'bg-gold text-black'
                      : ''
                  }`}
                  style={activeTab !== tab ? { color: 'var(--text-secondary)' } : {}}
                  data-testid={`tab-${tab}`}
                >
                  {t(`quran.tabs.${tab}`)}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 md:w-64">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="text"
                  placeholder={t('quran.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg text-sm"
                  data-testid="quran-search"
                />
              </div>

              {/* Sort */}
              {activeTab === 'surah' && (
                <div className="relative">
                  <button
                    onClick={() => setShowSort(!showSort)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}
                    data-testid="sort-btn"
                  >
                    {t('quran.sortBy')}
                    <ChevronDown size={14} />
                  </button>
                  {showSort && (
                    <div
                      className="absolute right-0 top-full mt-1 rounded-lg shadow-xl z-10 min-w-[160px] py-1"
                      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
                      data-testid="sort-dropdown"
                    >
                      {[
                        { value: 'default', label: language === 'sv' ? 'Standard' : 'Default' },
                        { value: 'name', label: language === 'sv' ? 'Namn' : 'Name' },
                        { value: 'ayahs', label: language === 'sv' ? 'Antal Ayah' : 'Ayah Count' },
                        { value: 'revelation', label: language === 'sv' ? 'Uppenbarelse' : 'Revelation' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setSortOrder(opt.value); setShowSort(false); }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gold/10 transition-colors"
                          style={{ color: sortOrder === opt.value ? 'var(--gold)' : 'var(--text-secondary)' }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          {activeTab === 'juz' ? (
            <div className="space-y-8" data-testid="juz-list">
              {juzSurahs.map(juz => (
                <div key={juz.number}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gold/10 border border-gold/30">
                      <span className="text-gold text-sm font-semibold">{juz.number}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {t('quran.juzLabel')} {juz.number}
                      </h3>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        {juz.name}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {juz.surahs
                      .filter(s => {
                        if (!searchQuery.trim()) return true;
                        const q = searchQuery.toLowerCase();
                        return s.name.toLowerCase().includes(q) || s.en.toLowerCase().includes(q) || s.sv.toLowerCase().includes(q);
                      })
                      .map(surah => (
                        <SurahCard key={surah.number} surah={surah} language={language} />
                      ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="surah-grid">
              {filteredSurahs.map(surah => (
                <SurahCard key={surah.number} surah={surah} language={language} />
              ))}
            </div>
          )}

          {filteredSurahs.length === 0 && activeTab !== 'juz' && (
            <div className="text-center py-20">
              <BookOpen size={48} className="mx-auto mb-4 text-gold opacity-30" />
              <p style={{ color: 'var(--text-muted)' }}>
                {language === 'sv' ? 'Inga suror hittades' : 'No surahs found'}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default QuranPage;
